import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Rating,
  Alert,
  CircularProgress,
  Stack,
  alpha
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ViewQuilt as SectionsIcon,
  Speed as PlansIcon,
  Campaign as CampaignIcon,
  LibraryBooks as ContentIcon,
  People as LeadsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';


// Estructura de pestañas principales
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`landing-tabpanel-${index}`}
      aria-labelledby={`landing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function LandingManager() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Token para autenticación
  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  }), []);

  const triggerAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  // ==========================================
  // ESTADOS POR PESTAÑA
  // ==========================================

  // Tab 0: Configuración & SEO
  const [settings, setSettings] = useState({
    company_name: 'YinoWISP', slogan: '', description: '',
    phone: '', whatsapp: '', email: '', address: '', opening_hours: '',
    logo_url: '', favicon_url: '',
    social_links: { facebook: '', instagram: '', youtube: '', twitter: '' },
    maintenance_mode: false, maintenance_message: '',
    custom_colors: { primary: '#6366f1', secondary: '#06b6d4', background: '#0b0f19' },
    seo_metadata: { title: '', description: '', keywords: '', og_image: '' },
    analytic_scripts: { gtm: '', google_analytics: '', pixel: '' }
  });

  // Tab 1: Secciones (Constructor Visual)
  const [sections, setSections] = useState([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  // Tab 2: Planes de Internet
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);

  // Tab 3: Campaña & Banners
  const [campaign, setCampaign] = useState({
    plan_id: '', title: '', subtitle: '', description: '',
    banner_url: '', image_url: '', campaign_color: '#ef4444',
    button_text: '¡Lo quiero!', button_url: '',
    start_date: '', end_date: '', is_active: false
  });
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerModalOpen, setBannerModalOpen] = useState(false);

  // Tab 4: Contenido Dinámico (FAQs, Testimonios)
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);

  // Tab 5: Leads (Prospectos)
  const [leads, setLeads] = useState([]);
  const [editingLead, setEditingLead] = useState(null);
  const [leadModalOpen, setLeadModalOpen] = useState(false);

  // ==========================================
  // CARGA DE DATOS
  // ==========================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Configuración General
      const settingsRes = await fetch('/api/landing/settings', { headers: getHeaders() });
      if (settingsRes.ok) {
        const sData = await settingsRes.json();
        if (sData.id) {
          setSettings({
            ...sData,
            social_links: typeof sData.social_links === 'string' ? JSON.parse(sData.social_links) : (sData.social_links || {}),
            custom_colors: typeof sData.custom_colors === 'string' ? JSON.parse(sData.custom_colors) : (sData.custom_colors || {}),
            seo_metadata: typeof sData.seo_metadata === 'string' ? JSON.parse(sData.seo_metadata) : (sData.seo_metadata || {}),
            analytic_scripts: typeof sData.analytic_scripts === 'string' ? JSON.parse(sData.analytic_scripts) : (sData.analytic_scripts || {})
          });
        }
      }

      // Secciones
      const sectionsRes = await fetch('/api/landing/sections', { headers: getHeaders() });
      if (sectionsRes.ok) setSections(await sectionsRes.json());

      // Planes
      const plansRes = await fetch('/api/plans', { headers: getHeaders() });
      if (plansRes.ok) setPlans(await plansRes.json());

      // Campaña
      const campaignsRes = await fetch('/api/landing/campaigns', { headers: getHeaders() });
      if (campaignsRes.ok) {
        const cData = await campaignsRes.json();
        const activeCampaign = cData.find(c => c.is_active) || cData[0];
        if (activeCampaign) {
          // Formatear fechas para los inputs de tipo date
          setCampaign({
            ...activeCampaign,
            start_date: activeCampaign.start_date ? activeCampaign.start_date.split('T')[0] : '',
            end_date: activeCampaign.end_date ? activeCampaign.end_date.split('T')[0] : ''
          });
        }
      }

      // Banners
      const bannersRes = await fetch('/api/landing/banners', { headers: getHeaders() });
      if (bannersRes.ok) setBanners(await bannersRes.json());

      // FAQs
      const faqsRes = await fetch('/api/landing/faq', { headers: getHeaders() });
      if (faqsRes.ok) setFaqs(await faqsRes.json());

      // Testimonios
      const testimonialsRes = await fetch('/api/landing/testimonials', { headers: getHeaders() });
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());

      // Leads
      const leadsRes = await fetch('/api/landing/leads', { headers: getHeaders() });
      if (leadsRes.ok) setLeads(await leadsRes.json());

    } catch (error) {
      console.error('Error al cargar datos del gestor de landing:', error);
      triggerAlert('Error al cargar la información del servidor.', 'error');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================
  // PESTAÑA 0: CONFIGURACIÓN GENERAL & SEO
  // ==========================================
  const saveSettings = async () => {
    try {
      const response = await fetch('/api/landing/settings', {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        triggerAlert('Configuración guardada exitosamente.');
      } else {
        triggerAlert('Error al guardar la configuración.', 'error');
      }
    } catch (error) {
      triggerAlert('Fallo la conexión con el servidor.', 'error');
    }
  };

  // ==========================================
  // PESTAÑA 1: SECCIONES (CONSTRUCTOR VISUAL)
  // ==========================================
  const moveSection = async (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSections.length) return;

    // Intercambiar orden_index
    const temp = newSections[index].order_index;
    newSections[index].order_index = newSections[targetIndex].order_index;
    newSections[targetIndex].order_index = temp;

    // Reordenar array local
    const [moved] = newSections.splice(index, 1);
    newSections.splice(targetIndex, 0, moved);

    setSections(newSections);

    // Guardar cambios en base de datos
    try {
      const response = await fetch('/api/landing/sections/reorder', {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ sections: newSections })
      });
      if (!response.ok) triggerAlert('Error al guardar el nuevo orden.', 'error');
    } catch (e) {
      triggerAlert('Fallo la conexión.', 'error');
    }
  };

  const toggleSectionActive = async (id, currentStatus) => {
    const updatedSections = sections.map(s => s.id === id ? { ...s, is_active: !currentStatus } : s);
    setSections(updatedSections);

    try {
      const response = await fetch('/api/landing/sections/reorder', {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ sections: updatedSections })
      });
      if (!response.ok) triggerAlert('Error al actualizar la visibilidad.', 'error');
    } catch (e) {
      triggerAlert('Error de conexión.', 'error');
    }
  };

  // Drag and Drop Event Handlers
  const handleDragStart = (e, idx) => {
    setDraggedItemIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragEnter = (targetIdx) => {
    if (draggedItemIndex === null || draggedItemIndex === targetIdx) return;
    
    const newSections = [...sections];
    const [draggedItem] = newSections.splice(draggedItemIndex, 1);
    newSections.splice(targetIdx, 0, draggedItem);
    
    const updatedSections = newSections.map((sec, idx) => ({
      ...sec,
      order_index: idx
    }));
    
    setSections(updatedSections);
    setDraggedItemIndex(targetIdx);
  };

  const handleDragEnd = async () => {
    setDraggedItemIndex(null);
    try {
      const response = await fetch('/api/landing/sections/reorder', {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ sections })
      });
      if (response.ok) {
        triggerAlert('Constructor: Estructura de la web actualizada.');
      } else {
        triggerAlert('Error al guardar el nuevo orden.', 'error');
      }
    } catch (e) {
      triggerAlert('Error de red al guardar el orden.', 'error');
    }
  };

  const sectionDescriptions = {
    hero: 'Banner principal con el eslogan de la empresa, descripción y botones de acceso principal.',
    planes: 'Selector interactivo de planes en Soles con control deslizante (slider) adaptado a cada velocidad.',
    campaña: 'Campaña especial del mes destacando un plan en primera plana con redirección de cotización a WhatsApp.',
    servicios: 'Detalle e información comercial de soluciones de conectividad para hogares y empresas.',
    beneficios: 'Ventajas de la red YinoWISP y simulación interactiva del Portal del Cliente (autogestión de pagos, consumos y averías).',
    estadísticas: 'Métricas de escala real (Clientes activos, Torres/Nodos operativos, Tickets resueltos).',
    testimonios: 'Opiniones y valoraciones con estrellas otorgadas por clientes del servicio.',
    faq: 'Sección de preguntas frecuentes organizadas en acordeones interactivos y fluidos.',
    contacto: 'Datos de contacto comercial de la empresa y formulario reactivo para captar leads (prospectos).'
  };


  // ==========================================
  // PESTAÑA 2: PLANES WEB
  // ==========================================
  const handleEditPlan = (plan) => {
    setEditingPlan({
      ...plan,
      caracteristicas_adicionales: typeof plan.caracteristicas_adicionales === 'string' 
        ? JSON.parse(plan.caracteristicas_adicionales) 
        : (plan.caracteristicas_adicionales || [])
    });
    setPlanModalOpen(true);
  };

  const savePlanEdit = async () => {
    try {
      const response = await fetch(`/api/plans/${editingPlan.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(editingPlan)
      });
      if (response.ok) {
        triggerAlert('Plan actualizado con éxito.');
        setPlanModalOpen(false);
        loadData();
      } else {
        triggerAlert('Error al guardar los cambios del plan.', 'error');
      }
    } catch (error) {
      triggerAlert('Error de conexión.', 'error');
    }
  };

  // ==========================================
  // PESTAÑA 3: CAMPAÑA & BANNERS
  // ==========================================
  const saveCampaign = async () => {
    try {
      const method = campaign.id ? 'PUT' : 'POST';
      const endpoint = campaign.id ? `/api/landing/campaigns/${campaign.id}` : '/api/landing/campaigns';
      
      const response = await fetch(endpoint, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(campaign)
      });
      if (response.ok) {
        triggerAlert('Campaña del mes guardada correctamente.');
        loadData();
      } else {
        triggerAlert('Error al guardar la campaña.', 'error');
      }
    } catch (e) {
      triggerAlert('Error al enviar los datos de la campaña.', 'error');
    }
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner ? {
      ...banner,
      start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
      end_date: banner.end_date ? banner.end_date.split('T')[0] : ''
    } : {
      image_url: '', title: '', description: '', button_text: '', button_url: '',
      priority: 0, start_date: '', end_date: '', is_active: true
    });
    setBannerModalOpen(true);
  };

  const saveBanner = async () => {
    try {
      const method = editingBanner.id ? 'PUT' : 'POST';
      const endpoint = editingBanner.id ? `/api/landing/banners/${editingBanner.id}` : '/api/landing/banners';
      const response = await fetch(endpoint, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(editingBanner)
      });
      if (response.ok) {
        triggerAlert('Banner guardado exitosamente.');
        setBannerModalOpen(false);
        loadData();
      } else {
        triggerAlert('Error al guardar el banner.', 'error');
      }
    } catch (e) {
      triggerAlert('Error de red.', 'error');
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este banner?')) return;
    try {
      const response = await fetch(`/api/landing/banners/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (response.ok) {
        triggerAlert('Banner eliminado.');
        loadData();
      } else {
        triggerAlert('Error al eliminar banner.', 'error');
      }
    } catch (e) {
      triggerAlert('Error de red.', 'error');
    }
  };

  // ==========================================
  // PESTAÑA 4: CONTENIDOS (FAQs, Testimonios)
  // ==========================================
  const handleEditFaq = (faq) => {
    setEditingFaq(faq || { question: '', answer: '', category: 'general', order_index: 0, is_active: true });
    setFaqModalOpen(true);
  };

  const saveFaq = async () => {
    try {
      const method = editingFaq.id ? 'PUT' : 'POST';
      const endpoint = editingFaq.id ? `/api/landing/faq/${editingFaq.id}` : '/api/landing/faq';
      const response = await fetch(endpoint, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(editingFaq)
      });
      if (response.ok) {
        triggerAlert('Pregunta FAQ guardada.');
        setFaqModalOpen(false);
        loadData();
      } else {
        triggerAlert('Error al guardar FAQ.', 'error');
      }
    } catch (e) {
      triggerAlert('Error de conexión.', 'error');
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta pregunta?')) return;
    try {
      const response = await fetch(`/api/landing/faq/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        triggerAlert('FAQ eliminada.');
        loadData();
      }
    } catch (e) {
      triggerAlert('Error de red.', 'error');
    }
  };

  const handleEditTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial || { client_name: '', comment: '', rating: 5, district: '', is_active: true, order_index: 0 });
    setTestimonialModalOpen(true);
  };

  const saveTestimonial = async () => {
    try {
      const method = editingTestimonial.id ? 'PUT' : 'POST';
      const endpoint = editingTestimonial.id ? `/api/landing/testimonials/${editingTestimonial.id}` : '/api/landing/testimonials';
      const response = await fetch(endpoint, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(editingTestimonial)
      });
      if (response.ok) {
        triggerAlert('Testimonio guardado exitosamente.');
        setTestimonialModalOpen(false);
        loadData();
      }
    } catch (e) {
      triggerAlert('Error de red.', 'error');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este testimonio?')) return;
    try {
      const response = await fetch(`/api/landing/testimonials/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        triggerAlert('Testimonio eliminado.');
        loadData();
      }
    } catch (e) {
      triggerAlert('Error de red.', 'error');
    }
  };

  // ==========================================
  // PESTAÑA 5: LEADS (PROSPECTOS)
  // ==========================================
  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setLeadModalOpen(true);
  };

  const updateLeadStatus = async () => {
    try {
      const response = await fetch(`/api/landing/leads/${editingLead.id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ status: editingLead.status, observations: editingLead.observations })
      });
      if (response.ok) {
        triggerAlert('Prospecto actualizado exitosamente.');
        setLeadModalOpen(false);
        loadData();
      }
    } catch (e) {
      triggerAlert('Error de conexión.', 'error');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Alerta flotante */}
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* Título de Sección */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Gestión de Landing Page
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Controla y personaliza todo el contenido público de yinowisp.com de manera dinámica.
          </Typography>
        </Box>
        {loading && <CircularProgress size={24} />}
      </Box>

      {/* Tabs Layout */}
      <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            <Tab icon={<SettingsIcon fontSize="small" />} iconPosition="start" label="Configuración & SEO" />
            <Tab icon={<SectionsIcon fontSize="small" />} iconPosition="start" label="Constructor" />
            <Tab icon={<PlansIcon fontSize="small" />} iconPosition="start" label="Planes Web" />
            <Tab icon={<CampaignIcon fontSize="small" />} iconPosition="start" label="Campaña & Banners" />
            <Tab icon={<ContentIcon fontSize="small" />} iconPosition="start" label="FAQs y Testimonios" />
            <Tab icon={<LeadsIcon fontSize="small" />} iconPosition="start" label="Prospectos" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {/* TAB 0: CONFIGURACIÓN GENERAL & SEO */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Datos Generales del Negocio</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nombre de la Empresa" value={settings.company_name} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Eslogan Comercial" value={settings.slogan} onChange={(e) => setSettings({ ...settings, slogan: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Descripción Corporativa" value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Teléfono de Contacto" value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="WhatsApp de Ventas" value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Correo Electrónico" value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Dirección Física" value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Horarios de Atención" value={settings.opening_hours} onChange={(e) => setSettings({ ...settings, opening_hours: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="URL del Logo Principal" value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />
            
            <Typography variant="h6" fontWeight="bold" gutterBottom>Metaetiquetas SEO y Analíticas</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Meta Título SEO" value={settings.seo_metadata?.title || ''} onChange={(e) => setSettings({ ...settings, seo_metadata: { ...settings.seo_metadata, title: e.target.value } })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Meta Palabras Clave" value={settings.seo_metadata?.keywords || ''} onChange={(e) => setSettings({ ...settings, seo_metadata: { ...settings.seo_metadata, keywords: e.target.value } })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Meta Descripción SEO" value={settings.seo_metadata?.description || ''} onChange={(e) => setSettings({ ...settings, seo_metadata: { ...settings.seo_metadata, description: e.target.value } })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Google Tag Manager (ID)" value={settings.analytic_scripts?.gtm || ''} onChange={(e) => setSettings({ ...settings, analytic_scripts: { ...settings.analytic_scripts, gtm: e.target.value } })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Meta Pixel ID" value={settings.analytic_scripts?.pixel || ''} onChange={(e) => setSettings({ ...settings, analytic_scripts: { ...settings.analytic_scripts, pixel: e.target.value } })} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" fontWeight="bold" gutterBottom>Modo de Mantenimiento</Typography>
            <Box sx={{ mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={settings.maintenance_mode} 
                    onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })} 
                  />
                }
                label="Activar Modo Mantenimiento en Web Pública"
              />
              {settings.maintenance_mode && (
                <TextField 
                  fullWidth 
                  multiline 
                  rows={2} 
                  sx={{ mt: 2 }}
                  label="Mensaje para los Visitantes" 
                  value={settings.maintenance_message} 
                  onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })} 
                />
              )}
            </Box>

            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveSettings} size="large">
              Guardar Configuración General
            </Button>
          </TabPanel>

          {/* TAB 1: CONSTRUCTOR VISUAL DE SECCIONES */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Estructura y Orden de la Landing Page</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Controla el orden de aparición y activa/desactiva secciones del constructor de cara a los visitantes.
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {sections.map((sec, idx) => {
                const isDragging = idx === draggedItemIndex;
                const primaryColor = settings.custom_colors?.primary || '#6366f1';
                
                return (
                  <Box
                    key={sec.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnd={handleDragEnd}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2.5,
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: isDragging ? primaryColor : '#e2e8f0',
                      bgcolor: isDragging ? alpha(primaryColor, 0.04) : '#ffffff',
                      boxShadow: isDragging ? `0 8px 25px ${alpha(primaryColor, 0.15)}` : '0 1px 3px rgba(0,0,0,0.02)',
                      opacity: isDragging ? 0.6 : 1,
                      transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      '&:hover': {
                        borderColor: primaryColor,
                        boxShadow: `0 4px 12px ${alpha(primaryColor, 0.08)}`
                      }
                    }}
                  >
                    {/* Drag Handle */}
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <DragIcon />
                    </Box>

                    {/* Section Info */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sec.title}
                        </Typography>
                        <Chip 
                          label={sec.section_key} 
                          size="small" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.75rem', 
                            bgcolor: '#f1f5f9',
                            color: '#64748b' 
                          }} 
                        />
                      </Stack>
                      <Typography variant="body2" color="textSecondary">
                        {sectionDescriptions[sec.section_key] || 'Bloque dinámico editable.'}
                      </Typography>
                    </Box>

                    {/* Switch Status */}
                    <Box sx={{ mx: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={sec.is_active} 
                            onChange={() => toggleSectionActive(sec.id, sec.is_active)}
                          />
                        }
                        label={sec.is_active ? 'Activa' : 'Desactivada'}
                        labelPlacement="start"
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {/* Reorder Fallback Arrows (Accessibility) */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small"
                        disabled={idx === 0} 
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, 'up'); }}
                      >
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        disabled={idx === sections.length - 1} 
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, 'down'); }}
                      >
                        <DownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

          </TabPanel>

          {/* TAB 2: PLANES WEB */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Planes de Internet en la Web</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Configura cuáles planes se muestran en la Landing, sus etiquetas, velocidad y características adicionales.
            </Typography>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre del Plan</TableCell>
                  <TableCell>Precio Mensual</TableCell>
                  <TableCell>Velocidad</TableCell>
                  <TableCell>Mostrar en Web</TableCell>
                  <TableCell>Es Destacado</TableCell>
                  <TableCell>Etiqueta</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(plan => (
                  <TableRow key={plan.id}>
                    <TableCell fontWeight="bold">{plan.name}</TableCell>
                    <TableCell>S/ {plan.price}</TableCell>
                    <TableCell>{plan.download_speed} Mbps</TableCell>
                    <TableCell>
                      <Chip 
                        label={plan.mostrar_en_web ? 'Sí' : 'No'} 
                        color={plan.mostrar_en_web ? 'success' : 'default'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {plan.es_destacado ? (
                        <Chip label="Destacado" color="primary" size="small" />
                      ) : '-'}
                    </TableCell>
                    <TableCell>{plan.etiqueta_web || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEditPlan(plan)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabPanel>

          {/* TAB 3: CAMPAÑA & BANNERS */}
          <TabPanel value={activeTab} index={3}>
            {/* Campaña del mes */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>Campaña del Mes en Primera Plana</Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Plan de Internet Promocionado</InputLabel>
                  <Select
                    value={campaign.plan_id || ''}
                    label="Plan de Internet Promocionado"
                    onChange={(e) => setCampaign({ ...campaign, plan_id: e.target.value })}
                  >
                    <MenuItem value=""><em>Ninguno</em></MenuItem>
                    {plans.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name} (S/ {p.price})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Título de la Campaña" value={campaign.title || ''} onChange={(e) => setCampaign({ ...campaign, title: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Subtítulo" value={campaign.subtitle || ''} onChange={(e) => setCampaign({ ...campaign, subtitle: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Color Temático (Hex)" value={campaign.campaign_color || ''} onChange={(e) => setCampaign({ ...campaign, campaign_color: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Descripción Comercial" value={campaign.description || ''} onChange={(e) => setCampaign({ ...campaign, description: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Fecha de Inicio" type="date" InputLabelProps={{ shrink: true }} value={campaign.start_date || ''} onChange={(e) => setCampaign({ ...campaign, start_date: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Fecha de Fin (Vencimiento)" type="date" InputLabelProps={{ shrink: true }} value={campaign.end_date || ''} onChange={(e) => setCampaign({ ...campaign, end_date: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={campaign.is_active} onChange={(e) => setCampaign({ ...campaign, is_active: e.target.checked })} />}
                  label="Activar Campaña en Primera Plana"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={saveCampaign}>
                  Guardar Campaña
                </Button>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Banners CRUD */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Carrusel de Banners Secundarios</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleEditBanner(null)}>
                Nuevo Banner
              </Button>
            </Box>

            <Table sx={{ mb: 4 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Miniatura</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {banners.map(b => (
                  <TableRow key={b.id}>
                    <TableCell>
                      {b.image_url ? (
                        <Box component="img" src={b.image_url} sx={{ width: 80, height: 40, objectFit: 'cover', borderRadius: '4px' }} />
                      ) : '-'}
                    </TableCell>
                    <TableCell fontWeight="bold">{b.title || 'Sin Título'}</TableCell>
                    <TableCell>{b.priority}</TableCell>
                    <TableCell>
                      {b.start_date ? b.start_date.split('T')[0] : 'Indefinida'} a {b.end_date ? b.end_date.split('T')[0] : 'Indefinida'}
                    </TableCell>
                    <TableCell>
                      <Chip label={b.is_active ? 'Activo' : 'Inactivo'} color={b.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEditBanner(b)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteBanner(b.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabPanel>

          {/* TAB 4: CONTENIDOS FAQs Y TESTIMONIOS */}
          <TabPanel value={activeTab} index={4}>
            {/* FAQs CRUD */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Preguntas Frecuentes (FAQs)</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleEditFaq(null)}>
                Agregar Pregunta
              </Button>
            </Box>

            <Table sx={{ mb: 5 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pregunta</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Orden</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {faqs.map(faq => (
                  <TableRow key={faq.id}>
                    <TableCell fontWeight="bold">{faq.question}</TableCell>
                    <TableCell>{faq.category}</TableCell>
                    <TableCell>{faq.order_index}</TableCell>
                    <TableCell>
                      <Chip label={faq.is_active ? 'Activo' : 'Inactivo'} color={faq.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEditFaq(faq)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteFaq(faq.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 4 }} />

            {/* Testimonios CRUD */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Testimonios de Clientes</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleEditTestimonial(null)}>
                Agregar Testimonio
              </Button>
            </Box>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Comentario</TableCell>
                  <TableCell>Valoración</TableCell>
                  <TableCell>Distrito</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testimonials.map(t => (
                  <TableRow key={t.id}>
                    <TableCell fontWeight="bold">{t.client_name}</TableCell>
                    <TableCell sx={{ maxWidth: 200, truncate: true }}>{t.comment}</TableCell>
                    <TableCell><Rating value={t.rating} readOnly size="small" /></TableCell>
                    <TableCell>{t.district || '-'}</TableCell>
                    <TableCell>
                      <Chip label={t.is_active ? 'Activo' : 'Inactivo'} color={t.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEditTestimonial(t)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteTestimonial(t.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabPanel>

          {/* TAB 5: LEADS (PROSPECTOS) */}
          <TabPanel value={activeTab} index={5}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Bandeja de Prospectos Captados (Leads)</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Aquí se listan los prospectos que rellenaron los formularios de cobertura, cotizaciones o contacto desde la web pública.
            </Typography>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Contacto</TableCell>
                  <TableCell>Distrito</TableCell>
                  <TableCell>Plan de Interés</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map(lead => (
                  <TableRow key={lead.id}>
                    <TableCell>{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                    <TableCell fontWeight="bold">{lead.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.phone || '-'}</Typography>
                      <Typography variant="caption" color="textSecondary">{lead.email || '-'}</Typography>
                    </TableCell>
                    <TableCell>{lead.district || '-'}</TableCell>
                    <TableCell>{lead.interest_plan || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={lead.status} 
                        color={
                          lead.status === 'Nuevo' ? 'warning' :
                          lead.status === 'Contactado' ? 'info' :
                          lead.status === 'Cliente' ? 'success' : 'default'
                        } 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button variant="outlined" size="small" onClick={() => handleEditLead(lead)}>
                        Gestionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabPanel>
        </CardContent>
      </Card>

      {/* ==========================================
      MODALES / DIÁLOGOS DE EDICIÓN
      ========================================== */}

      {/* Modal Editar Plan */}
      <Dialog open={planModalOpen} onClose={() => setPlanModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Parámetros Comerciales del Plan</DialogTitle>
        <DialogContent dividers>
          {editingPlan && (
            <Stack spacing={3} sx={{ py: 1 }}>
              <Typography variant="subtitle2" color="primary">Plan: <strong>{editingPlan.name}</strong></Typography>
              
              <FormControlLabel
                control={<Switch checked={editingPlan.mostrar_en_web} onChange={(e) => setEditingPlan({ ...editingPlan, mostrar_en_web: e.target.checked })} />}
                label="Mostrar en la Landing Page Pública"
              />
              <FormControlLabel
                control={<Switch checked={editingPlan.es_destacado} onChange={(e) => setEditingPlan({ ...editingPlan, es_destacado: e.target.checked })} />}
                label="Destacar Plan (Mayor tamaño e insignia especial)"
              />
              <TextField fullWidth label="Etiqueta Comercial (Ej. 'El más popular', 'Mejor precio')" value={editingPlan.etiqueta_web || ''} onChange={(e) => setEditingPlan({ ...editingPlan, etiqueta_web: e.target.value })} />
              <TextField fullWidth label="Uso Recomendado" value={editingPlan.uso_recomendado || ''} onChange={(e) => setEditingPlan({ ...editingPlan, uso_recomendado: e.target.value })} />
              <TextField fullWidth label="Dispositivos Sugeridos" value={editingPlan.dispositivos_sugeridos || ''} onChange={(e) => setEditingPlan({ ...editingPlan, dispositivos_sugeridos: e.target.value })} />
              <TextField fullWidth label="Color de Destacado (Hex)" value={editingPlan.color_resaltado || '#6366f1'} onChange={(e) => setEditingPlan({ ...editingPlan, color_resaltado: e.target.value })} />
              <TextField fullWidth type="number" label="Orden en Web (0 = Primero)" value={editingPlan.orden_web || 0} onChange={(e) => setEditingPlan({ ...editingPlan, orden_web: parseInt(e.target.value) || 0 })} />
              
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Beneficios Comerciales del Plan (uno por línea):</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Velocidad Simétrica&#10;Router Dual Band Gratis&#10;Instalación Express"
                value={editingPlan.caracteristicas_adicionales?.join('\n') || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, caracteristicas_adicionales: e.target.value.split('\n') })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePlanEdit}>Guardar Cambios del Plan</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Banner */}
      <Dialog open={bannerModalOpen} onClose={() => setBannerModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBanner?.id ? 'Editar Banner' : 'Crear Nuevo Banner'}</DialogTitle>
        <DialogContent dividers>
          {editingBanner && (
            <Stack spacing={3} sx={{ py: 1 }}>
              <TextField fullWidth label="URL de Imagen (Requerido)" value={editingBanner.image_url} onChange={(e) => setEditingBanner({ ...editingBanner, image_url: e.target.value })} />
              <TextField fullWidth label="Título del Banner" value={editingBanner.title} onChange={(e) => setEditingBanner({ ...editingBanner, title: e.target.value })} />
              <TextField fullWidth multiline rows={2} label="Descripción" value={editingBanner.description} onChange={(e) => setEditingBanner({ ...editingBanner, description: e.target.value })} />
              <TextField fullWidth label="Texto del Botón CTA" value={editingBanner.button_text} onChange={(e) => setEditingBanner({ ...editingBanner, button_text: e.target.value })} />
              <TextField fullWidth label="URL de Destino del Botón" value={editingBanner.button_url} onChange={(e) => setEditingBanner({ ...editingBanner, button_url: e.target.value })} />
              <TextField fullWidth type="number" label="Prioridad (Mayor = Sale primero)" value={editingBanner.priority} onChange={(e) => setEditingBanner({ ...editingBanner, priority: parseInt(e.target.value) || 0 })} />
              <TextField fullWidth label="Vigencia Inicio" type="date" InputLabelProps={{ shrink: true }} value={editingBanner.start_date} onChange={(e) => setEditingBanner({ ...editingBanner, start_date: e.target.value })} />
              <TextField fullWidth label="Vigencia Fin" type="date" InputLabelProps={{ shrink: true }} value={editingBanner.end_date} onChange={(e) => setEditingBanner({ ...editingBanner, end_date: e.target.value })} />
              <FormControlLabel
                control={<Switch checked={editingBanner.is_active} onChange={(e) => setEditingBanner({ ...editingBanner, is_active: e.target.checked })} />}
                label="Banner Activo"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBannerModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveBanner}>Guardar Banner</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar FAQ */}
      <Dialog open={faqModalOpen} onClose={() => setFaqModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFaq?.id ? 'Editar Pregunta FAQ' : 'Agregar Pregunta FAQ'}</DialogTitle>
        <DialogContent dividers>
          {editingFaq && (
            <Stack spacing={3} sx={{ py: 1 }}>
              <TextField fullWidth label="Pregunta" value={editingFaq.question} onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })} />
              <TextField fullWidth multiline rows={4} label="Respuesta" value={editingFaq.answer} onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })} />
              <TextField fullWidth label="Categoría" value={editingFaq.category} onChange={(e) => setEditingFaq({ ...editingFaq, category: e.target.value })} />
              <TextField fullWidth type="number" label="Orden" value={editingFaq.order_index} onChange={(e) => setEditingFaq({ ...editingFaq, order_index: parseInt(e.target.value) || 0 })} />
              <FormControlLabel
                control={<Switch checked={editingFaq.is_active} onChange={(e) => setEditingFaq({ ...editingFaq, is_active: e.target.checked })} />}
                label="FAQ Activo"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveFaq}>Guardar FAQ</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Testimonio */}
      <Dialog open={testimonialModalOpen} onClose={() => setTestimonialModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTestimonial?.id ? 'Editar Testimonio' : 'Agregar Testimonio'}</DialogTitle>
        <DialogContent dividers>
          {editingTestimonial && (
            <Stack spacing={3} sx={{ py: 1 }}>
              <TextField fullWidth label="Nombre del Cliente" value={editingTestimonial.client_name} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, client_name: e.target.value })} />
              <TextField fullWidth label="Distrito" value={editingTestimonial.district} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, district: e.target.value })} />
              <TextField fullWidth label="URL del Avatar" value={editingTestimonial.avatar_url || ''} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, avatar_url: e.target.value })} />
              <TextField fullWidth multiline rows={3} label="Comentario" value={editingTestimonial.comment} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, comment: e.target.value })} />
              <Box>
                <Typography component="legend">Calificación (Estrellas)</Typography>
                <Rating value={editingTestimonial.rating} onChange={(e, val) => setEditingTestimonial({ ...editingTestimonial, rating: val })} />
              </Box>
              <FormControlLabel
                control={<Switch checked={editingTestimonial.is_active} onChange={(e) => setEditingTestimonial({ ...editingTestimonial, is_active: e.target.checked })} />}
                label="Testimonio Activo"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestimonialModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveTestimonial}>Guardar Testimonio</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Gestionar Lead */}
      <Dialog open={leadModalOpen} onClose={() => setLeadModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gestionar Prospecto / Lead</DialogTitle>
        <DialogContent dividers>
          {editingLead && (
            <Stack spacing={2.5} sx={{ py: 1 }}>
              <Typography variant="subtitle1">Cliente: <strong>{editingLead.name}</strong></Typography>
              <Typography variant="body2">Teléfono: {editingLead.phone || '-'}</Typography>
              <Typography variant="body2">Dirección: {editingLead.address || '-'} ({editingLead.district || '-'})</Typography>
              <Typography variant="body2">Plan de Interés: {editingLead.interest_plan || '-'}</Typography>
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Estado del Lead</InputLabel>
                <Select
                  value={editingLead.status}
                  label="Estado del Lead"
                  onChange={(e) => setEditingLead({ ...editingLead, status: e.target.value })}
                >
                  <MenuItem value="Nuevo">Nuevo</MenuItem>
                  <MenuItem value="Contactado">Contactado</MenuItem>
                  <MenuItem value="Interesado">Interesado</MenuItem>
                  <MenuItem value="Instalación Programada">Instalación Programada</MenuItem>
                  <MenuItem value="Cliente">Convertido en Cliente</MenuItem>
                  <MenuItem value="Descartado">Descartado</MenuItem>
                </Select>
              </FormControl>

              <TextField 
                fullWidth 
                multiline 
                rows={3} 
                label="Observaciones y Seguimiento" 
                value={editingLead.observations || ''} 
                onChange={(e) => setEditingLead({ ...editingLead, observations: e.target.value })} 
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeadModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={updateLeadStatus}>Actualizar Prospecto</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
